import React, { useEffect, useState } from "react";
import { usersApi } from "../services/users";
import type { ReferralCodeLookup } from "../types/User";
import { cleanReferralCode, clearInviteCode, readInviteCode } from "../lib/referralInvite";
import { absMediaUrl } from "../utils/mediaUrl";
import "./ReferralInvite.css";
import { t as tr } from "../i18n";

// Refer & Earn at signup: a /join link's code we know is real → the "Invited
// by DJ Lucky" chip — or, for one of our launch-offer codes (1.5.1 #12), "0%
// launch offer applied". No typed code box since 10/30 (founder: "they will
// see the referred-by badge anyway"). Never blocks signup: an unknown code
// shows nothing and is sent as it is (the server ignores it).

type Referrer = ReferralCodeLookup;

export function useReferralInvite(initial: string) {
  const [code, setCode] = useState(() => cleanReferralCode(initial) || readInviteCode());
  const [referrer, setReferrer] = useState<Referrer | null>(null);
  useEffect(() => {
    if (code.length < 2) { setReferrer(null); return; }
    let cancelled = false;
    // Small pause so typing LUCKY is one lookup, not five.
    const t = setTimeout(() => {
      usersApi.referrer(code).then((r) => { if (!cancelled) setReferrer(r); }).catch(() => { if (!cancelled) setReferrer(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [code]);
  // Dropping the code (the chip's ×) forgets the saved /join code too, or the
  // bank step would still send it (it falls back to the saved code for
  // sign-ups that resume past this screen; 2026-10-10).
  const change = (v: string) => {
    const next = cleanReferralCode(v);
    if (!next) clearInviteCode();
    setCode(next);
  };
  return { code, setCode: change, referrer };
}

export function InvitedByChip({ referrer, onRemove }: { referrer: Referrer; onRemove: () => void }) {
  const src = referrer.offer ? undefined : absMediaUrl(referrer.avatarUrl || undefined);
  return (
    <div className="invite-chip">
      {referrer.offer ? (
        // A launch-offer code belongs to nobody: a check where a face would be.
        <i className="invite-chip-mark" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
        </i>
      ) : src ? (
        <img src={src} alt="" />
      ) : (
        <i>{(referrer.name || "?")[0].toUpperCase()}</i>
      )}
      <span className="invite-chip-name">
        {referrer.offer
          ? tr("referralInvite.launchOfferApplied")
          : referrer.name
            ? tr("referralInvite.invitedBy", { name: referrer.name })
            : tr("referralInvite.invitedByAFan")}
      </span>
      {/* Wrong person? One tap drops the invite. */}
      <button type="button" aria-label={tr("referralInvite.removeReferralCode")} onClick={() => { clearInviteCode(); onRemove(); }}>
        ×
      </button>
    </div>
  );
}
